import {
  Button,
  Center,
  ConditionalRendering,
  Loader,
  Text
} from "@marketplace/ui";
import { notifications } from "@marketplace/ui/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import clsx from "clsx";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HiPlus } from "react-icons/hi";

import { NoProductIllustraction } from "@/assets/images/no-product-illustration";
import { Pagination } from "@/components";

import {
  addProductModalAtom,
  automationModalAtom,
  changePublishStatusModalAtom,
  deleteProductModalAtom,
  platformFeeInputModalAtom,
  setPromoModalAtom
} from "../../atoms/product-list-page-atom";
import { AddProductModal } from "../../components/add-product-modal";
import { ChangePublishStatusModal } from "../../components/change-publish-status-modal";
import { DeleteProductModal } from "../../components/delete-product-modal";
import { Filters } from "../../components/filters";
import type { PlatformFeeData } from "../../components/platform-fee-input-modal/platform-fee-input-modal";
import { PlatformFeeInputModal } from "../../components/platform-fee-input-modal/platform-fee-input-modal";
import { PlatformFeeSummaryModal } from "../../components/platform-fee-summary-modal/platform-fee-summary-modal";
import { ProductAutomationModal } from "../../components/product-automation-modal";
import { ProductListTable } from "../../components/product-list-table";
import { SetPromoModal } from "../../components/set-promo-modal";
import { ProductQueryKeys } from "../../generators/product-query-keys";
import { useGetProductDetailQuery } from "../../hooks/queries/use-get-product-detail-query";
import { useGetProductDraftListQuery } from "../../hooks/queries/use-get-product-draft-list-query";
import { useGetProductListQuery } from "../../hooks/queries/use-get-product-list-query";
import { mapToProductList } from "../../utils/product-transformers";

const route = getRouteApi("/products/list/");

type NoProductProps = {
  handleAddProduct: () => void;
};

const NoProduct: React.FC<NoProductProps> = ({ handleAddProduct }) => {
  const { t } = useTranslation("product");

  return (
    <div className="flex flex-col justify-center items-center gap-y-6 mt-[56px]">
      <NoProductIllustraction />
      <div>
        <Text type="heading" size="xl" className="mb-2 text-fjord-grey-500">
          {t("productListPage.emptyProduct.title")}
        </Text>
        <Text type="paragraph" size="l" className="text-fjord-grey-300">
          {t("productListPage.emptyProduct.description")}
        </Text>
      </div>
      <Button
        onClick={handleAddProduct}
        variant="primary"
        rightSection={<HiPlus size={16} />}
      >
        {t("productListPage.addProduct")}
      </Button>
    </div>
  );
};

type ProductTabProps = {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
};

const ProductTab: React.FC<ProductTabProps> = ({
  label,
  count,
  isActive,
  onClick
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex items-center gap-x-2 px-[10px] py-[6px] rounded-full border-solid border transition-all cursor-pointer",
        isActive
          ? "border-noble-blue-500 bg-cerulean-blue-100"
          : "border-fjord-grey-100 bg-brand-white hover:bg-fjord-grey-50"
      )}
    >
      <span
        className={clsx(
          "w-1 h-1 rounded-full",
          isActive ? "bg-[#34C759]" : "bg-fjord-grey-100"
        )}
      />
      <Text
        type="heading"
        size="ss"
        weight="semibold"
        className={clsx(
          isActive ? "text-noble-blue-500" : "text-fjord-grey-400"
        )}
      >
        {label}
      </Text>
      <div className="p-1 rounded-full bg-mischka-100 h-[22px] min-w-[22px] flex items-center justify-center">
        <Text
          size="xs"
          className={clsx(
            isActive ? "text-noble-blue-500" : "text-fjord-grey-400"
          )}
        >
          {count}
        </Text>
      </div>
    </button>
  );
};

export const ProductListPage = () => {
  const { tab, page, q, sort, draft_page, draft_q, draft_sort } =
    route.useSearch();
  const navigate = route.useNavigate();
  const { t } = useTranslation("product");
  const [{ opened: addProductModalVisible }, setAddProductModal] =
    useAtom(addProductModalAtom);
  const { visible: deleteProductModalVisible } = useAtomValue(
    deleteProductModalAtom
  );
  const automationModal = useAtomValue(automationModalAtom);
  const changePublishStatusModal = useAtomValue(changePublishStatusModalAtom);
  const setPromoModal = useAtomValue(setPromoModalAtom);
  const platformFeeModal = useAtomValue(platformFeeInputModalAtom);
  const setPlatformFeeModal = useSetAtom(platformFeeInputModalAtom);
  const resetAddProductModal = useResetAtom(addProductModalAtom);
  const resetDeleteProductModal = useResetAtom(deleteProductModalAtom);
  const resetAutomationModal = useResetAtom(automationModalAtom);
  const resetChangePublishStatusModal = useResetAtom(
    changePublishStatusModalAtom
  );
  const resetSetPromoModal = useResetAtom(setPromoModalAtom);

  const [currentPackageIndex, setCurrentPackageIndex] = useState(1);
  const [platformFeeData, setPlatformFeeData] = useState<PlatformFeeData[]>([]);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useGetProductListQuery({
    queryParams: {
      page,
      limit: 10,
      q,
      sort
    }
  });

  const { data: draftData, isLoading: draftLoading } =
    useGetProductDraftListQuery({
      queryParams: {
        page: draft_page,
        limit: 10,
        q: draft_q,
        sort: draft_sort
      }
    });

  const handleAddProduct = () => {
    setAddProductModal({ opened: true });
  };

  const handleTabChange = (newTab: "product" | "draft") => {
    navigate({
      search: (prev) => ({
        ...prev,
        tab: newTab
      })
    });
  };

  const { data: productDetailData, isLoading: productDetailLoading } =
    useGetProductDetailQuery({
      queryParams: { slug: platformFeeModal.productSlug || "" },
      options: {
        enabled: !!platformFeeModal.productSlug
      }
    });

  const handleCloseFeeSelectionModal = () => {
    setCurrentPackageIndex(1);
    setPlatformFeeData([]);
    setPlatformFeeModal({ opened: false, productSlug: null });
  };

  const handleFeeSelectionBack = () => {
    setCurrentPackageIndex((prev) => Math.max(1, prev - 1));
  };

  const handleFeeSelectionNext = (data: PlatformFeeData) => {
    setPlatformFeeData((prev) => {
      const indexToUpdate = currentPackageIndex - 1;
      if (prev[indexToUpdate]) {
        return [...prev.slice(0, indexToUpdate), data];
      }
      return [...prev, data];
    });
    const totalPackages = productDetailData?.data?.product_package?.length ?? 1;

    if (currentPackageIndex < totalPackages) {
      setCurrentPackageIndex((prev) => prev + 1);
    } else {
      setPlatformFeeModal({
        opened: false,
        productSlug: platformFeeModal.productSlug
      });
      setIsSummaryModalOpen(true);
    }
  };
  const handleSummarySaveSuccess = () => {
    const productName = productDetailData?.data?.name;
    setIsSummaryModalOpen(false);
    setCurrentPackageIndex(1);
    setPlatformFeeData([]);
    setPlatformFeeModal({ opened: false, productSlug: null });
    if (tab === "product") {
      queryClient.invalidateQueries({
        queryKey: ProductQueryKeys.getProductList({
          page,
          limit: 10,
          q,
          sort: "newest"
        }).queryKey
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: ProductQueryKeys.getProductDraftList({
          page,
          limit: 10,
          q,
          sort: "newest"
        }).queryKey
      });
    }
    notifications.show({
      message: `Successfully updated ${productName ?? "the product"} target margin`
    });
  };

  const handleSummaryBack = () => {
    setPlatformFeeModal({
      opened: true,
      productSlug: platformFeeModal.productSlug
    });
    setIsSummaryModalOpen(false);
  };

  const productListData = data?.data;
  const draftProductListData = draftData?.data;
  const metadata = data?.metadata;
  const draftMetadata = draftData?.metadata;
  const productCount = metadata?.total ?? 0;
  const draftCount = draftMetadata?.total ?? 0;

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <Text type="heading" size="l" className="text-humble-blue-500">
          {t("productListPage.title")}
        </Text>
        <Button
          variant="primary"
          rightSection={<HiPlus size={16} />}
          onClick={handleAddProduct}
        >
          {t("productListPage.addProduct")}
        </Button>
      </div>

      <div className="flex items-center gap-x-2 mb-2">
        <Text
          type="heading"
          size="xxs"
          weight="bold"
          className="text-fjord-grey-500 mr-2"
        >
          Section
        </Text>
        <ProductTab
          label="Product"
          count={productCount}
          isActive={tab === "product"}
          onClick={() => handleTabChange("product")}
        />
        <ProductTab
          label="Draft"
          count={draftCount}
          isActive={tab === "draft"}
          onClick={() => handleTabChange("draft")}
        />
      </div>

      <Filters />

      <ConditionalRendering condition={tab === "product"}>
        <ConditionalRendering condition={isLoading}>
          <Center className="my-20">
            <Loader />
          </Center>
        </ConditionalRendering>

        <ConditionalRendering
          condition={
            !isLoading && productListData && productListData.length > 0
          }
        >
          <div className="space-y-6">
            <ProductListTable
              records={mapToProductList(productListData ?? [])}
            />

            <Pagination
              currentPageTotal={productListData?.length}
              totalData={metadata?.total}
              total={metadata?.totalPage ?? 0}
              value={metadata?.page}
              onChange={(value) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    page: value
                  })
                })
              }
            />
          </div>
        </ConditionalRendering>

        <ConditionalRendering
          condition={!isLoading && !productListData?.length}
        >
          <NoProduct handleAddProduct={handleAddProduct} />
        </ConditionalRendering>
      </ConditionalRendering>

      <ConditionalRendering condition={tab === "draft"}>
        <ConditionalRendering condition={draftLoading}>
          <Center className="my-20">
            <Loader />
          </Center>
        </ConditionalRendering>

        <ConditionalRendering
          condition={
            !draftLoading &&
            draftProductListData &&
            draftProductListData.length > 0
          }
        >
          <div className="space-y-6">
            <ProductListTable
              records={mapToProductList(draftProductListData ?? [])}
              isDraft={true}
            />

            <Pagination
              currentPageTotal={draftProductListData?.length}
              totalData={draftMetadata?.total}
              total={draftMetadata?.totalPage ?? 0}
              value={draftMetadata?.page}
              onChange={(value) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    draft_page: value
                  })
                })
              }
            />
          </div>
        </ConditionalRendering>

        <ConditionalRendering
          condition={!draftLoading && !draftProductListData?.length}
        >
          <NoProduct handleAddProduct={handleAddProduct} />
        </ConditionalRendering>
      </ConditionalRendering>

      <AddProductModal
        opened={addProductModalVisible}
        onClose={resetAddProductModal}
      />
      <DeleteProductModal
        opened={deleteProductModalVisible}
        onClose={resetDeleteProductModal}
      />
      <ProductAutomationModal
        opened={automationModal.opened}
        onClose={resetAutomationModal}
        productId={automationModal.productId}
        isAutomationActive={automationModal.isAutomationActive}
      />
      <ChangePublishStatusModal
        opened={changePublishStatusModal.opened}
        onClose={resetChangePublishStatusModal}
      />
      <SetPromoModal
        opened={setPromoModal.opened}
        onClose={resetSetPromoModal}
      />
      <PlatformFeeInputModal
        key={platformFeeModal.opened ? currentPackageIndex : "closed"}
        packageData={productDetailData?.data?.product_package ?? []}
        packageNumber={currentPackageIndex}
        totalPackages={productDetailData?.data?.product_package?.length ?? 1}
        opened={platformFeeModal.opened}
        onClose={handleCloseFeeSelectionModal}
        onNext={handleFeeSelectionNext}
        onBack={handleFeeSelectionBack}
        isEditMode={true}
        productDetailLoading={productDetailLoading}
        pksFile={productDetailData?.data?.store_attributes?.pks_file}
        savedData={platformFeeData[currentPackageIndex - 1]}
      />
      <PlatformFeeSummaryModal
        opened={isSummaryModalOpen}
        onClose={() => {
          setIsSummaryModalOpen(false);
          setCurrentPackageIndex(1);
          setPlatformFeeData([]);
        }}
        onSaveSuccess={handleSummarySaveSuccess}
        onBack={handleSummaryBack}
        packages={platformFeeData}
        isEditMode={true}
      />
    </>
  );
};
